import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { HelpCircle, Plus, Eye, X } from "lucide-react";

export function GameShowTriviaPanel({ show, isHost }) {
  const [category, setCategory] = useState("General");
  const [points, setPoints] = useState(100);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [awardTarget, setAwardTarget] = useState({});

  const questions = show.trivia?.questions || [];

  const addQuestion = async () => {
    if (!question.trim()) return toast.error("Write a question first");
    try {
      await api.post(`/game-shows/${show.id}/trivia/questions`, { category, points, question_text: question, answer_text: answer });
      setQuestion("");
      setAnswer("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  };

  const reveal = (id) => api.patch(`/game-shows/${show.id}/trivia/questions/${id}`, { revealed: true });
  const remove = (id) => api.delete(`/game-shows/${show.id}/trivia/questions/${id}`);
  const award = async (q, correct) => {
    const participantId = awardTarget[q.id];
    if (!participantId) return toast.error("Pick who answered first");
    await api.post(`/game-shows/${show.id}/trivia/questions/${q.id}/award`, { participant_id: participantId, correct });
  };

  return (
    <div className="panel-wood rounded-2xl p-5 space-y-3" data-testid="gameshow-trivia-panel">
      <h2 className="font-display text-xl flex items-center gap-2"><HelpCircle className="w-4 h-4 text-[#D4AF37]" /> Trivia Board</h2>
      {questions.length === 0 && <p className="text-xs text-[#f1e0c5]/30">No questions yet.</p>}
      {questions.map((q) => (
        <div key={q.id} data-testid={`gameshow-trivia-question-${q.id}`} className="bg-black/20 rounded-lg p-3 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase text-[#f1e0c5]/40">{q.category} · {q.points} pts</span>
            {isHost && <button data-testid={`gameshow-trivia-delete-${q.id}`} onClick={() => remove(q.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>}
          </div>
          {(q.revealed || isHost) ? (
            <p className="text-sm">{q.question_text}</p>
          ) : (
            <p className="text-sm text-[#f1e0c5]/30 italic">Hidden until the host reveals it.</p>
          )}
          {isHost && q.answer_text && <p className="text-xs text-[#4d7c0f]">Answer: {q.answer_text}</p>}
          {q.answered_by_participant_id ? (
            <p className={`text-xs ${q.answered_correctly ? "text-[#4d7c0f]" : "text-[#D90429]"}`}>
              {(show.participants || []).find((p) => p.id === q.answered_by_participant_id)?.name || "Someone"} {q.answered_correctly ? "answered correctly" : "missed it"}
            </p>
          ) : isHost ? (
            <div className="flex gap-1.5 items-center pt-1">
              {!q.revealed && <Button data-testid={`gameshow-trivia-reveal-${q.id}`} size="sm" variant="secondary" className="rounded-full h-7 text-xs gap-1" onClick={() => reveal(q.id)}><Eye className="w-3 h-3" /> Reveal</Button>}
              <Select value={awardTarget[q.id] || ""} onValueChange={(v) => setAwardTarget((a) => ({ ...a, [q.id]: v }))}>
                <SelectTrigger data-testid={`gameshow-trivia-award-target-${q.id}`} className="bg-black/30 border-white/10 h-7 text-xs flex-1"><SelectValue placeholder="Who answered?" /></SelectTrigger>
                <SelectContent>{(show.participants || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid={`gameshow-trivia-correct-${q.id}`} size="sm" className="rounded-full h-7 text-xs bg-[#4d7c0f] hover:bg-[#3d6208]" onClick={() => award(q, true)}>Correct</Button>
              <Button data-testid={`gameshow-trivia-wrong-${q.id}`} size="sm" className="rounded-full h-7 text-xs bg-[#D90429] hover:bg-[#b8031f]" onClick={() => award(q, false)}>Miss</Button>
            </div>
          ) : null}
        </div>
      ))}
      {isHost && (
        <div className="border-t border-white/10 pt-3 space-y-2">
          <div className="flex gap-1.5">
            <Input data-testid="gameshow-trivia-category-input" placeholder="Category" value={category} onChange={(e) => setCategory(e.target.value)} className="bg-black/30 border-white/10 h-8 text-xs flex-1" />
            <Input data-testid="gameshow-trivia-points-input" type="number" value={points} onChange={(e) => setPoints(parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-8 text-xs w-20" />
          </div>
          <Input data-testid="gameshow-trivia-question-input" placeholder="Question" value={question} onChange={(e) => setQuestion(e.target.value)} className="bg-black/30 border-white/10 text-sm" />
          <Input data-testid="gameshow-trivia-answer-input" placeholder="Answer (host-only reference)" value={answer} onChange={(e) => setAnswer(e.target.value)} className="bg-black/30 border-white/10 text-sm" />
          <Button data-testid="gameshow-trivia-add-button" size="sm" className="rounded-full gap-1.5 bg-[#3F9142] hover:bg-[#357a39]" onClick={addQuestion}><Plus className="w-3.5 h-3.5" /> Add Question</Button>
        </div>
      )}
    </div>
  );
}
